import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { TeamPageTable } from './TeamPageTable';
import { AddMemberDialog } from './AddMemberDialog';
import { exportTeamToCSV } from '@/components/TeamImportExport';
import { useParams, useNavigate } from 'react-router-dom';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { Button } from '@/components/ui/button';
import { PageTransition } from '@/components/PageTransition';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/supabase';
import { useSpaceData } from '@/hooks/useSpaceData';
import { useAuth } from '@/contexts/AuthContext';
import { AccessDenied } from '@/components/AccessDenied';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { toast } from 'sonner';
import {
  Users,
  Shield,
  CheckCircle,
  UserPlus,
  Search,
  Download,
} from 'lucide-react';

const TeamPage = ({
  stackId,
  stackName,
  stackImage,
  stackProjects,
  isStackView = false
}) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { canManageSpace, userProfile, company, loading: authLoading } = useAuth();

  // Permission state
  const [hasAccess, setHasAccess] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);

  // For stack view, use stackProjects; otherwise use normal projectId
  const effectiveProjectId = isStackView ? null : projectId;

  // Memoize projectIds to prevent infinite re-renders
  const projectIds = useMemo(() => {
    return isStackView ? stackProjects?.map(p => p.id) : [projectId];
  }, [isStackView, stackProjects, projectId]);

  // Use the cached space data hook (only for non-stack views)
  // Pass company.id to validate the space belongs to the user's current company
  const {
    space,
    spaceTools,
    loading: spaceLoading,
    accessDenied
  } = useSpaceData(effectiveProjectId, userProfile?.id, company?.id);

  // Set page title
  usePageTitle(isStackView ? `${stackName} Team` : space?.name ? `${space.name} Team` : 'Team');

  // Redirect if user doesn't have access (e.g., switched companies)
  useEffect(() => {
    if (accessDenied) {
      toast.error('You no longer have access to this space');
      navigate('/dashboard', { replace: true });
    }
  }, [accessDenied, navigate]);

  // Space filter for stack view
  const [selectedSpaceFilter, setSelectedSpaceFilter] = useState('all');

  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceTimerRef = useRef(null);

  // Debounce search input (300ms)
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  // Check access permission on mount
  useEffect(() => {
    const checkAccess = async () => {
      // Stack views have different access patterns - skip check for now
      if (isStackView) {
        setHasAccess(true);
        setCheckingAccess(false);
        return;
      }

      // Wait for auth to finish loading before checking access
      if (authLoading) {
        return;
      }

      // If no projectId, we can't check access
      if (!projectId) {
        setCheckingAccess(false);
        return;
      }

      // Wait for userProfile to be available
      if (!userProfile?.id) {
        // Auth is done loading but no user profile - deny access
        setHasAccess(false);
        setCheckingAccess(false);
        return;
      }

      try {
        const canManage = await canManageSpace(projectId);
        setHasAccess(canManage);
      } catch (error) {
        console.error('Error checking space access:', error);
        setHasAccess(false);
      } finally {
        setCheckingAccess(false);
      }
    };

    checkAccess();
  }, [projectId, userProfile?.id, isStackView, canManageSpace, authLoading]);

  // Fetch compliance stats from unified RPC
  const fetchComplianceStats = useCallback(async (targetProjectId) => {
    if (!targetProjectId) return null;

    try {
      const { data, error } = await supabase.rpc(
        'calculate_compliance_stats',
        { p_project_id: targetProjectId }
      );

      if (error) {
        console.error('Error fetching compliance stats:', error);
        return null;
      }

      return data?.by_user || [];
    } catch (error) {
      console.error('Error in fetchComplianceStats:', error);
      return null;
    }
  }, []);

  // Merge compliance stats with team members by user_id
  const mergeComplianceWithMembers = useCallback((members, complianceByUser) => {
    if (!complianceByUser || complianceByUser.length === 0) {
      return members;
    }

    // Create a map for quick lookup
    const complianceMap = new Map(
      complianceByUser.map(c => [c.user_id, c])
    );

    return members.map(member => {
      const userId = member.user_id || member.id;
      const complianceData = complianceMap.get(userId);

      if (complianceData) {
        return {
          ...member,
          compliance: complianceData.compliance_percentage ?? 0,
          compliance_percentage: complianceData.compliance_percentage ?? 0,
          assigned_count: complianceData.assigned_count,
          valid: complianceData.valid,
          expiring: complianceData.expiring,
          expired: complianceData.expired,
          missing: complianceData.missing,
          valid_cross_space: complianceData.valid_cross_space,
          expiring_cross_space: complianceData.expiring_cross_space,
          requirements: {
            total: complianceData.assigned_count || 0,
            completed: (complianceData.valid || 0) + (complianceData.valid_cross_space || 0),
            pending: (complianceData.missing || 0) + (complianceData.expiring || 0) + (complianceData.expiring_cross_space || 0),
            expired: complianceData.expired || 0
          }
        };
      }

      return {
        ...member,
        compliance: member.compliance ?? 0,
        compliance_percentage: member.compliance_percentage ?? 0
      };
    });
  }, []);

  // Reusable fetch function for team members
  const fetchTeamMembers = useCallback(async () => {
    try {
      setLoading(true);

      if (isStackView && projectIds && projectIds.length > 0) {
        // Stack view: Use batch RPC (single query instead of N queries)
        const { data: members, error } = await supabase.rpc(
          'get_team_members_with_stats_batch',
          { p_project_ids: projectIds }
        );

        if (error) {
          console.error('Error fetching team members (batch):', error);
          setTeamMembers([]);
        } else {
          // Fetch compliance stats for each project in parallel
          const compliancePromises = projectIds.map(pid => fetchComplianceStats(pid));
          const complianceResults = await Promise.all(compliancePromises);

          // Flatten all compliance data into single array
          const allComplianceByUser = complianceResults
            .filter(Boolean)
            .flat();

          const formattedMembers = (members || []).map(m => ({
            ...m,
            lastActive: m.last_active
              ? new Date(m.last_active).toLocaleString()
              : 'Never'
          }));

          // Merge compliance data
          const membersWithCompliance = mergeComplianceWithMembers(formattedMembers, allComplianceByUser);
          setTeamMembers(membersWithCompliance);
        }
      } else if (projectId && userProfile?.id) {
        // Single project view - Fetch team members and compliance stats in parallel
        const [spaceDataResult, complianceByUser] = await Promise.all([
          supabase.rpc('get_space_page_data', {
            p_project_id: projectId,
            p_user_id: userProfile.id
          }),
          fetchComplianceStats(projectId)
        ]);

        const { data, error: spaceError } = spaceDataResult;

        if (spaceError) {
          console.error('Error fetching space data:', spaceError);
          setTeamMembers([]);
        } else if (data && data.team_members) {
          const formattedMembers = (data.team_members || []).map(member => ({
            ...member,
            name: `${member.first_name || ''} ${member.last_name || ''}`.trim(),
            avatarUrl: member.avatar_url,
            lastActive: member.last_active
              ? new Date(member.last_active).toLocaleString()
              : 'Never'
          }));

          // Merge compliance data from unified RPC
          const membersWithCompliance = mergeComplianceWithMembers(formattedMembers, complianceByUser);
          setTeamMembers(membersWithCompliance);
        } else {
          setTeamMembers([]);
        }
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, userProfile?.id, isStackView, projectIds, fetchComplianceStats, mergeComplianceWithMembers]);

  // Load team members on mount
  useEffect(() => {
    if (projectId || (isStackView && projectIds && projectIds.length > 0)) {
      fetchTeamMembers();
    }
  }, [fetchTeamMembers]);

  // Handle edit member
  const handleEditMember = useCallback(member => {
    navigate(`/space/${projectId}/team/edit/${member.id}`);
  }, [navigate, projectId]);

  // Handle manage requirements - navigate to profile qualifications tab with spaceId filter
  const handleManageRequirements = useCallback(member => {
    const userId = member.user_id || member.id;
    navigate(`/profile/${userId}?tab=qualifications&spaceId=${projectId}`);
  }, [navigate, projectId]);

  // Handle member deletion
  const handleDeleteMembers = useCallback(async membersToDelete => {
    try {
      const userIds = membersToDelete
        .map(m => m.user_id || m.id)
        .filter(Boolean);

      if (userIds.length === 0) return false;

      const { error } = await supabase
        .from('space_members')
        .delete()
        .in('user_id', userIds)
        .eq('project_id', projectId);

      if (error) {
        console.error('Error deleting space members:', error);
        return false;
      }

      // Update local state
      setTeamMembers(prev => prev.filter(
        m => !userIds.includes(m.user_id || m.id)
      ));

      return true;
    } catch (error) {
      console.error('Error in handleDeleteMembers:', error);
      return false;
    }
  }, [projectId]);

  // Filter by selected space (for stack view)
  const spaceFilteredMembers = useMemo(() => {
    if (!isStackView || selectedSpaceFilter === 'all') {
      return teamMembers;
    }
    return teamMembers.filter(m => m.project_id === selectedSpaceFilter);
  }, [teamMembers, isStackView, selectedSpaceFilter]);

  // Filter team members based on debounced search (memoized)
  const filteredMembers = useMemo(() => {
    if (!debouncedSearch.trim()) {
      return spaceFilteredMembers;
    }
    const searchLower = debouncedSearch.toLowerCase();
    return spaceFilteredMembers.filter(
      member =>
        (member.name || '').toLowerCase().includes(searchLower) ||
        (member.email || '').toLowerCase().includes(searchLower) ||
        (member.role || '').toLowerCase().includes(searchLower)
    );
  }, [spaceFilteredMembers, debouncedSearch]);

  // Memoized team stats to avoid recalculation on every render
  const teamStats = useMemo(() => {
    const totalMembers = teamMembers.length;
    const activeMembers = teamMembers.filter(m => m.status === 'active').length;
    const avgCompliance = totalMembers > 0
      ? Math.round(teamMembers.reduce((acc, m) => acc + (m.compliance || 0), 0) / totalMembers)
      : 0;
    return {
      totalMembers,
      activeMembers,
      avgCompliance
    };
  }, [teamMembers]);

  // Get compliance color
  const getComplianceColor = compliance => {
    if (compliance >= 90) return 'text-green-600 bg-green-50';
    if (compliance >= 70) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  // Get status badge variant
  const getStatusVariant = status => {
    return status === 'active' ? 'default' : 'secondary';
  };

  // Show loading while auth is loading, user profile not available, or checking access
  if (authLoading || !userProfile || checkingAccess) {
    return (
      <AppShell3 pageTitle='' pageDescription=''>
        <div className="min-h-[50vh] flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  // Show access denied if user doesn't have permission (hasAccess is explicitly false, not null)
  if (hasAccess === false && !isStackView) {
    return (
      <AppShell3>
        <AccessDenied
          message="You don't have permission to view team members for this space. Only admins and assigned managers can access this page."
          backTo={projectId ? `/space/${projectId}` : '/dashboard'}
          backLabel={projectId ? 'Back to Space' : 'Back to Dashboard'}
        />
      </AppShell3>
    );
  }

  return (
    <AppShell3 pageTitle='' pageDescription=''>
      <PageTransition>
        <ToolHeader
          spaceName={isStackView ? stackName : space?.name || 'Space'}
          spaceImage={isStackView ? stackImage : space?.logo_url}
          toolName='Team'
          currentTool='team'
          spaceTools={isStackView ? [] : spaceTools}
          stackId={stackId}
          stackProjects={isStackView ? stackProjects : []}
          isStackView={isStackView}
          selectedSpaceFilter={selectedSpaceFilter}
          onSpaceFilterChange={setSelectedSpaceFilter}
          actions={
            <div className='flex items-center gap-3'>
              <div className='relative'>
                <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
                <Input
                  type='text'
                  placeholder='Search team members...'
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className='pl-10 bg-white w-64'
                />
              </div>
              <Button
                variant='outline'
                size='sm'
                onClick={() =>
                  exportTeamToCSV(
                    teamMembers,
                    isStackView ? stackName : space?.name
                  )
                }
              >
                <Download className='h-4 w-4 mr-2' />
                Export Team
              </Button>
              <Button
                size='sm'
                onClick={() => setShowAddMember(true)}
              >
                <UserPlus className='h-4 w-4 mr-2' />
                Add Member
              </Button>
            </div>
          }
        />

        {/* Main Content Container */}
        <div className='max-w-7xl mx-auto mt-4'>
          {/* Team Stats - using memoized values */}
          <div className='grid grid-cols-4 gap-4 mb-6'>
            <div className='bg-white p-4 rounded-lg border'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-500'>Total Members</p>
                  <p className='text-2xl font-bold'>{teamStats.totalMembers}</p>
                </div>
                <Users className='h-8 w-8 text-gray-400' />
              </div>
            </div>
            <div className='bg-white p-4 rounded-lg border'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-500'>Active</p>
                  <p className='text-2xl font-bold text-green-600'>
                    {teamStats.activeMembers}
                  </p>
                </div>
                <CheckCircle className='h-8 w-8 text-green-400' />
              </div>
            </div>
            <div className='bg-white p-4 rounded-lg border'>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-500'>Avg Compliance</p>
                  <p className='text-2xl font-bold'>
                    {teamStats.avgCompliance}%
                  </p>
                </div>
                <Shield className='h-8 w-8 text-blue-400' />
              </div>
            </div>
          </div>

          {/* Team Members Table - UntitledUI Style */}
          <TeamPageTable
            members={filteredMembers}
            setMembers={setTeamMembers}
            onDeleteMembers={handleDeleteMembers}
            onEditMember={handleEditMember}
            onManageRequirements={handleManageRequirements}
            projectId={projectId}
            loading={loading}
          />

          {/* Add Member Dialog */}
          <AddMemberDialog
            open={showAddMember}
            onOpenChange={setShowAddMember}
            projectId={projectId}
            isStackView={isStackView}
            stackProjects={stackProjects}
            onAddMember={async newMember => {
              if (newMember && newMember.id) {
                // Optimistically add the new member
                setTeamMembers(prev => {
                  const exists = prev.some(m => m.id === newMember.id);
                  if (exists) return prev;
                  return [...prev, newMember];
                });
              } else if (newMember === null) {
                // CSV import or bulk add completed - refetch using reusable function
                await fetchTeamMembers();
              }
            }}
          />
        </div>
      </PageTransition>
    </AppShell3>
  );
};

export default TeamPage;
